import type { DocNode } from '@atlaskit/adf-schema/doc';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import type { ConfluenceUpdatePageCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-update-page-command';

import type { DocumentUpdateCommandProcessor } from '../../stream-processors/update-command-processor';
import type { AbortOptions, AbortReason } from '../../types/abort-reason';
import type { AIFC_ADF_UPDATE_KEY } from '../../types/command-manager';
import { getAgentAttribution, getAgentUpdateMetadata } from '../../utils/get-agent-attribution';
import type { PageUpdateStrategy } from '../page-update-strategy';

type AdfUpdatePayload = Extract<
	ConfluenceUpdatePageCommandProperties,
	{ key: typeof AIFC_ADF_UPDATE_KEY }
>;

export class AdfUpdateStrategy implements PageUpdateStrategy {
	constructor(private processor: DocumentUpdateCommandProcessor) {}

	startAction(strictFifo: boolean, onExecutionTimeout?: () => void): void {
		this.processor.startAction(strictFifo, onExecutionTimeout);
	}

	async process(payload: ConfluenceUpdatePageCommandProperties): Promise<void> {
		const {
			data,
			isStreamComplete,
			traceId,
			singleInstrumentationId,
			isInlineEdit,
			isChatEdit,
			cwrRemixInvokedFrom,
		} = payload as AdfUpdatePayload;
		const metadata = getAgentUpdateMetadata(payload);
		if (!isExperimentEnabled('platform_editor_ai_streaming_ux_experience_m1')) {
			metadata.agentAttribution = getAgentAttribution({
				agentType: payload.agentType,
				agentIdentityAccountId: payload.agentIdentityAccountId,
			});
			if (metadata.agentInfo) {
				metadata.agentInfo.isThirdParty = undefined;
			}
		}
		const { agentInfo, agentAttribution } = metadata;
		const isBrowserEligibleCompletion =
			isExperimentEnabled('platform_editor_ai_tool_call_context') &&
			isStreamComplete &&
			(data as typeof data & { appliedServerSide?: boolean }).appliedServerSide === false;
		// `creationExperience` stays at its positional slot; browser eligibility follows it.
		const trailingArgs: [creationExperience?: string, isBrowserEligibleCompletion?: true] =
			isBrowserEligibleCompletion
				? [payload.creationExperience, true]
				: payload.creationExperience !== undefined
					? [payload.creationExperience]
					: [];
		await this.processor.streamChunk(
			data.toolCalls,
			isStreamComplete,
			traceId,
			singleInstrumentationId,
			agentInfo,
			isInlineEdit,
			agentAttribution,
			isChatEdit,
			cwrRemixInvokedFrom,
			...trailingArgs,
		);
	}

	async finish(): Promise<DocNode | undefined> {
		await this.processor.finish();
		return undefined;
	}

	abort(reason?: AbortReason, options?: AbortOptions): void {
		this.processor.abort(reason, options);
	}
}
